import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Building2, Trash2, Key as KeyIcon, Copy, Check, Image as ImageIcon, RefreshCw, Save, ArrowLeft, QrCode, Search, UserPlus, Users, Download, Wand2 } from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const OrgDetail = ({ org, onBack, onChanged }) => {
  const [current, setCurrent] = useState(org);
  const [name, setName] = useState(org.name || '');
  const [maxPingmes, setMaxPingmes] = useState(org.max_pingmes ?? 100000);
  const [isActive, setIsActive] = useState(org.is_active !== false);
  const [branding, setBranding] = useState(org.branding || {});
  const [admins, setAdmins] = useState(org.admins || []);
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [pingmes, setPingmes] = useState([]);
  const [loadingPingmes, setLoadingPingmes] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [genCount, setGenCount] = useState(1);
  const [genLabel, setGenLabel] = useState('perdu ? Scannez moi');
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState([]);

  const reloadOrg = async () => {
    try {
      const res = await axios.get(`${API}/admin/organizations/${org.org_id}`);
      setCurrent(res.data);
      setAdmins(res.data.admins || []);
    } catch { /* ignore */ }
  };

  const reloadPingmes = async () => {
    setLoadingPingmes(true);
    try {
      const res = await axios.get(`${API}/admin/organizations/${org.org_id}/pingmes?limit=50`);
      setPingmes(res.data.pingmes || []);
    } catch { /* ignore */ }
    finally { setLoadingPingmes(false); }
  };

  useEffect(() => { reloadPingmes(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [org.org_id]);

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      const res = await axios.patch(`${API}/admin/organizations/${org.org_id}`, {
        name, max_pingmes: Number(maxPingmes), is_active: isActive,
      });
      setCurrent(res.data);
      toast.success('Organisation mise a jour');
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSavingInfo(false); }
  };

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      const res = await axios.put(`${API}/admin/organizations/${org.org_id}/branding`, branding);
      setBranding(res.data.branding);
      toast.success('Branding mis a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSavingBranding(false); }
  };

  const regenerateKey = async () => {
    if (!window.confirm('Regenerer la cle API ? L\'ancienne sera invalidee immediatement.')) return;
    try {
      const res = await axios.post(`${API}/admin/organizations/${org.org_id}/api-key`);
      setCurrent({ ...current, api_key: res.data.api_key });
      toast.success('Nouvelle cle API generee');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const copyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(current.api_key);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    } catch {
      toast.error('Copie impossible');
    }
  };

  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBranding({ ...branding, [field]: res.data.url });
    } catch (err) {
      toast.error('Upload impossible');
    } finally { setUploading(false); }
  };

  const resolveUrl = (u) => (u && u.startsWith('/api') ? `${API.replace('/api', '')}${u}` : u);
  const pingmeCount = current.pingme_count ?? 0;

  return (
    <div data-testid="org-detail-view">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm font-bold text-charcoal/70 hover:text-charcoal mb-4" data-testid="org-back-btn">
        <ArrowLeft className="w-4 h-4" /> Retour a la liste
      </button>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Infos & Quotas */}
        <div className="card-brutal space-y-3">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Building2 className="w-5 h-5" /> Informations</h3>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Nom</label>
            <input className="input-brutal w-full" value={name} onChange={(e) => setName(e.target.value)} data-testid="org-name-input" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Quota PingMes (max)</label>
            <input type="number" className="input-brutal w-full" value={maxPingmes} onChange={(e) => setMaxPingmes(e.target.value)} min="1" data-testid="org-max-pingmes-input" />
            <p className="text-xs text-charcoal/50 mt-1">
              Utilises: <span className="font-bold text-charcoal">{pingmeCount}</span> / <span className="font-bold">{maxPingmes}</span>
            </p>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid="org-is-active-input" />
              <span className="text-sm font-bold">Organisation active</span>
            </label>
          </div>
          <button onClick={saveInfo} disabled={savingInfo} className="btn-primary text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="org-save-info-btn">
            <Save className="w-4 h-4" /> {savingInfo ? 'Sauvegarde...' : 'Enregistrer'}
          </button>
        </div>

        {/* API Key */}
        <div className="card-brutal space-y-3">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><KeyIcon className="w-5 h-5" /> Cle API</h3>
          <p className="text-xs text-charcoal/60">
            Donnez cette cle a l'organisation pour qu'elle puisse generer ses PingMes via l'API.
          </p>
          <div className="bg-charcoal/5 border-2 border-charcoal/20 rounded-xl p-3 font-mono text-xs break-all" data-testid="org-api-key-display">
            {current.api_key || '(cle manquante)'}
          </div>
          <div className="flex gap-2">
            <button onClick={copyApiKey} className="btn-outline text-sm px-4 py-2 flex items-center gap-2" data-testid="org-copy-api-key-btn">
              {apiKeyCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {apiKeyCopied ? 'Copie !' : 'Copier'}
            </button>
            <button onClick={regenerateKey} className="btn-outline text-sm px-4 py-2 flex items-center gap-2 text-danger border-danger" data-testid="org-regen-api-key-btn">
              <RefreshCw className="w-4 h-4" /> Regenerer
            </button>
          </div>
          <details className="text-xs text-charcoal/70">
            <summary className="cursor-pointer font-bold">Exemple d'appel API</summary>
            <pre className="mt-2 bg-charcoal text-white p-3 rounded-lg overflow-x-auto text-[10px]">
{`curl -X POST "${window.location.origin}/api/org-api/pingmes" \\
  -H "X-Org-Api-Key: ${current.api_key || 'YOUR_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"count": 10, "label_text": "Scannez moi"}'`}
            </pre>
          </details>
        </div>
      </div>

      {/* Branding */}
      <div className="card-brutal mt-4 space-y-3">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Branding (affiche lors du scan)</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Nom affiche</label>
            <input className="input-brutal w-full" value={branding.display_name || ''} onChange={(e) => setBranding({ ...branding, display_name: e.target.value })} data-testid="branding-display-name" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Couleur primaire (hex)</label>
            <div className="flex items-center gap-2">
              <input type="color" className="w-10 h-10 border-2 border-charcoal rounded-lg" value={branding.primary_color || '#FF6B35'} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} data-testid="branding-primary-color" />
              <input className="input-brutal flex-1" value={branding.primary_color || ''} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} placeholder="#FF6B35" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Tagline (courte phrase)</label>
            <input className="input-brutal w-full" value={branding.tagline || ''} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} data-testid="branding-tagline" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Message complet</label>
            <textarea className="input-brutal w-full" rows={3} value={branding.message || ''} onChange={(e) => setBranding({ ...branding, message: e.target.value })} data-testid="branding-message" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">URL site web</label>
            <input className="input-brutal w-full" value={branding.website_url || ''} onChange={(e) => setBranding({ ...branding, website_url: e.target.value })} placeholder="https://..." data-testid="branding-website" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Logo</label>
            <div className="flex items-center gap-2">
              {branding.logo_url && <img src={resolveUrl(branding.logo_url)} alt="logo" className="w-12 h-12 rounded-lg border-2 border-charcoal object-cover" />}
              <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0], 'logo_url')} className="text-xs" data-testid="branding-logo-upload" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Image de couverture (optionnelle)</label>
            <div className="flex items-center gap-2">
              {branding.cover_image_url && <img src={resolveUrl(branding.cover_image_url)} alt="cover" className="h-16 rounded-lg border-2 border-charcoal object-cover" />}
              <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0], 'cover_image_url')} className="text-xs" data-testid="branding-cover-upload" />
            </div>
          </div>
        </div>
        <button onClick={saveBranding} disabled={savingBranding || uploading} className="btn-primary text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="org-save-branding-btn">
          <Save className="w-4 h-4" /> {savingBranding ? 'Sauvegarde...' : 'Enregistrer le branding'}
        </button>
      </div>

      {/* Admins of the organization */}
      <div className="card-brutal mt-4 space-y-3">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Users className="w-5 h-5" /> Admins de l'organisation ({admins.length})</h3>
        <p className="text-xs text-charcoal/60">Les admins peuvent acceder a cette organisation depuis leur dashboard et generer des PingMes directement.</p>
        {admins.length > 0 && (
          <ul className="divide-y divide-charcoal/10 border border-charcoal/10 rounded-xl overflow-hidden" data-testid="org-admins-list">
            {admins.map((a) => (
              <li key={a.user_id} className="flex items-center justify-between gap-2 px-3 py-2 bg-white">
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{a.name || a.email}</p>
                  <p className="text-xs text-charcoal/50 truncate">{a.email}</p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Retirer ${a.email} des admins ?`)) return;
                    try {
                      await axios.delete(`${API}/admin/organizations/${org.org_id}/admins/${a.user_id}`);
                      toast.success('Admin retire');
                      await reloadOrg();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                  }}
                  className="text-danger hover:bg-danger/10 p-1.5 rounded-lg"
                  data-testid={`remove-admin-${a.user_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!newAdminEmail.trim()) return;
            setAddingAdmin(true);
            try {
              await axios.post(`${API}/admin/organizations/${org.org_id}/admins`, { email: newAdminEmail.trim() });
              toast.success('Admin ajoute');
              setNewAdminEmail('');
              await reloadOrg();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Erreur');
            } finally { setAddingAdmin(false); }
          }}
          className="flex gap-2"
        >
          <input
            type="email"
            className="input-brutal flex-1"
            placeholder="email@exemple.com"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            required
            data-testid="add-admin-email-input"
          />
          <button type="submit" disabled={addingAdmin} className="btn-primary text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="add-admin-btn">
            <UserPlus className="w-4 h-4" /> {addingAdmin ? '...' : 'Ajouter'}
          </button>
        </form>
      </div>

      {/* Generate PingMes directly */}
      <div className="card-brutal mt-4 space-y-3">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Wand2 className="w-5 h-5" /> Generer des PingMes</h3>
        <p className="text-xs text-charcoal/60">Creez directement des PingMes non-enroles pour cette organisation.</p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setGenerating(true);
            setLastGenerated([]);
            try {
              const res = await axios.post(`${API}/admin/organizations/${org.org_id}/generate-pingmes`, {
                count: Number(genCount) || 1,
                label_text: genLabel || undefined,
              });
              toast.success(`${res.data.count} PingMe(s) generes`);
              setLastGenerated(res.data.generated || []);
              await reloadPingmes();
              await reloadOrg();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Erreur');
            } finally { setGenerating(false); }
          }}
          className="grid md:grid-cols-3 gap-3"
        >
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Quantite</label>
            <input type="number" min="1" max="500" className="input-brutal w-full" value={genCount} onChange={(e) => setGenCount(e.target.value)} data-testid="gen-count-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Texte sur le QR (optionnel)</label>
            <input className="input-brutal w-full" value={genLabel} onChange={(e) => setGenLabel(e.target.value)} data-testid="gen-label-input" />
          </div>
          <div className="md:col-span-3">
            <button type="submit" disabled={generating} className="btn-primary text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="gen-submit-btn">
              <Wand2 className="w-4 h-4" /> {generating ? 'Generation...' : 'Generer les PingMes'}
            </button>
          </div>
        </form>
        {lastGenerated.length > 0 && (
          <div className="border-2 border-green-300 bg-green-50 rounded-xl p-3" data-testid="generated-results">
            <p className="text-sm font-bold text-green-800 mb-2">{lastGenerated.length} PingMe(s) cree(s) :</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 max-h-80 overflow-y-auto">
              {lastGenerated.map((g) => (
                <div key={g.pingme_id} className="bg-white border border-charcoal/20 rounded-lg p-2 text-center" data-testid={`generated-qr-${g.pingme_id}`}>
                  <img src={`data:image/png;base64,${g.qr_code_base64}`} alt={g.pingme_id} className="w-full aspect-square object-contain" />
                  <p className="text-[9px] font-mono text-charcoal/60 truncate mt-1">{g.pingme_id}</p>
                  <a href={`data:image/png;base64,${g.qr_code_base64}`} download={`${g.pingme_id}.png`} className="inline-flex items-center gap-1 text-[10px] text-primary underline mt-1">
                    <Download className="w-3 h-3" /> PNG
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Org PingMes */}
      <div className="card-brutal mt-4">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><QrCode className="w-5 h-5" /> PingMes de cette organisation ({pingmes.length})</h3>
        {loadingPingmes ? (
          <p className="text-sm text-charcoal/60">Chargement...</p>
        ) : pingmes.length === 0 ? (
          <p className="text-sm text-charcoal/60">Aucun PingMe pour l'instant. L'organisation doit les generer via son API.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="org-pingmes-table">
              <thead>
                <tr className="text-left border-b-2 border-charcoal/10">
                  <th className="py-2 px-2">ID</th>
                  <th className="py-2 px-2">Nom</th>
                  <th className="py-2 px-2">Proprietaire</th>
                  <th className="py-2 px-2">Scans</th>
                  <th className="py-2 px-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {pingmes.map((p) => (
                  <tr key={p.pingme_id} className="border-b border-charcoal/5">
                    <td className="py-2 px-2 font-mono text-xs">{p.pingme_id}</td>
                    <td className="py-2 px-2">{p.name || <span className="italic text-charcoal/40">non enrole</span>}</td>
                    <td className="py-2 px-2 text-xs">{p.user_id || <span className="italic text-charcoal/40">—</span>}</td>
                    <td className="py-2 px-2">{p.scan_count || 0}</td>
                    <td className="py-2 px-2">
                      {p.is_active ? <span className="text-green-600 font-bold text-xs">Actif</span> : <span className="text-danger font-bold text-xs">Inactif</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const OrganizationsTab = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', max_pingmes: 100000 });
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCreatePartner, setShowCreatePartner] = useState(false);
  const [newPartner, setNewPartner] = useState({ company_name: '', contact_name: '', email: '', password: '', partner_specialty: '', send_welcome_email: true });
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [partnerCreated, setPartnerCreated] = useState(null); // {api_key, email, ...} shown after creation
  const [apiKeyCopied, setApiKeyCopied] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/organizations`, { params: { search: search || '' } });
      setOrgs(res.data.organizations || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const createOrg = async (e) => {
    e.preventDefault();
    if (!newOrg.name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/organizations`, {
        name: newOrg.name.trim(),
        max_pingmes: Number(newOrg.max_pingmes) || 100000,
      });
      toast.success('Organisation creee');
      setNewOrg({ name: '', max_pingmes: 100000 });
      setShowCreate(false);
      await fetchOrgs();
      setSelected(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setCreating(false); }
  };

  const createPartner = async (e) => {
    e.preventDefault();
    if (!newPartner.company_name.trim() || !newPartner.contact_name.trim() || !newPartner.email.trim() || newPartner.password.length < 8) {
      toast.error('Tous les champs sont requis (mot de passe min. 8 caracteres)');
      return;
    }
    setCreatingPartner(true);
    try {
      const res = await axios.post(`${API}/admin/partners`, {
        company_name: newPartner.company_name.trim(),
        contact_name: newPartner.contact_name.trim(),
        email: newPartner.email.trim().toLowerCase(),
        password: newPartner.password,
        partner_specialty: newPartner.partner_specialty.trim() || null,
        send_welcome_email: !!newPartner.send_welcome_email,
      });
      toast.success(res.data.welcome_email_sent ? 'Partenaire cree et email envoye' : 'Partenaire cree');
      setPartnerCreated(res.data);
      setNewPartner({ company_name: '', contact_name: '', email: '', password: '', partner_specialty: '', send_welcome_email: true });
      await fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setCreatingPartner(false); }
  };

  const copyApiKey = async () => {
    if (!partnerCreated?.api_key) return;
    try {
      await navigator.clipboard.writeText(partnerCreated.api_key);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const deleteOrg = async (org) => {
    if (!window.confirm(`Supprimer "${org.name}" ? Les PingMes lies seront conserves mais ne seront plus rattaches a une organisation.`)) return;
    try {
      await axios.delete(`${API}/admin/organizations/${org.org_id}`);
      toast.success('Organisation supprimee');
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (selected) {
    return <OrgDetail org={selected} onBack={() => { setSelected(null); fetchOrgs(); }} onChanged={fetchOrgs} />;
  }

  return (
    <div data-testid="admin-organizations-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-xl">Organisations</h2>
          <p className="text-sm text-charcoal/60">Gerez les marques/entreprises partenaires, leur quota et leur branding.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setShowCreatePartner(false); setShowCreate(!showCreate); }} className="btn-outline text-sm flex items-center gap-2" data-testid="create-org-btn">
            <Plus className="w-4 h-4" /> Nouvelle organisation
          </button>
          <button onClick={() => { setShowCreate(false); setShowCreatePartner(!showCreatePartner); }} className="btn-primary text-sm flex items-center gap-2" data-testid="create-partner-btn">
            <UserPlus className="w-4 h-4" /> Creer un partenaire
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
        <input
          className="input-brutal w-full pl-10"
          placeholder="Rechercher par nom..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="org-search-input"
        />
      </div>

      {showCreate && (
        <form onSubmit={createOrg} className="card-brutal mb-4 space-y-3" data-testid="create-org-form">
          <h3 className="font-heading font-bold">Creer une organisation</h3>
          <input className="input-brutal w-full" placeholder="Nom de l'organisation" value={newOrg.name} onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })} required data-testid="new-org-name-input" />
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Quota max de PingMes</label>
            <input type="number" min="1" className="input-brutal w-full" value={newOrg.max_pingmes} onChange={(e) => setNewOrg({ ...newOrg, max_pingmes: e.target.value })} data-testid="new-org-max-input" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={creating} className="btn-primary text-sm px-5 py-2 disabled:opacity-50" data-testid="submit-org-btn">
              {creating ? 'Creation...' : 'Creer'}
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className="btn-outline text-sm px-5 py-2">Annuler</button>
          </div>
        </form>
      )}

      {showCreatePartner && !partnerCreated && (
        <form onSubmit={createPartner} className="card-brutal mb-4 space-y-3" data-testid="create-partner-form">
          <div>
            <h3 className="font-heading font-bold flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" /> Creer un partenaire</h3>
            <p className="text-xs text-charcoal/60 mt-1">Cree un compte partenaire complet (utilisateur + organisation use_case=partner + cle API).</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Nom de la societe *</label>
              <input className="input-brutal w-full" placeholder="Acme Lunettes SAS" value={newPartner.company_name} onChange={(e) => setNewPartner({ ...newPartner, company_name: e.target.value })} required data-testid="new-partner-company" />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Specialite (optionnel)</label>
              <input className="input-brutal w-full" placeholder="ex: lunetier, equipementier..." value={newPartner.partner_specialty} onChange={(e) => setNewPartner({ ...newPartner, partner_specialty: e.target.value })} data-testid="new-partner-specialty" />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Nom du contact *</label>
              <input className="input-brutal w-full" placeholder="Jean Dupont" value={newPartner.contact_name} onChange={(e) => setNewPartner({ ...newPartner, contact_name: e.target.value })} required data-testid="new-partner-contact-name" />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Email du contact *</label>
              <input type="email" className="input-brutal w-full" placeholder="contact@acme.fr" value={newPartner.email} onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })} required data-testid="new-partner-email" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/70 mb-1 block">Mot de passe initial * (min. 8 caracteres)</label>
            <input type="text" className="input-brutal w-full font-mono" placeholder="A communiquer au partenaire pour sa premiere connexion" value={newPartner.password} onChange={(e) => setNewPartner({ ...newPartner, password: e.target.value })} required minLength={8} data-testid="new-partner-password" />
          </div>
          <label className="flex items-start gap-2 cursor-pointer bg-surface-alt/60 p-3 rounded-lg border border-charcoal/10" data-testid="new-partner-send-email-wrapper">
            <input
              type="checkbox"
              checked={newPartner.send_welcome_email}
              onChange={(e) => setNewPartner({ ...newPartner, send_welcome_email: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-primary"
              data-testid="new-partner-send-email-checkbox"
            />
            <div className="text-sm">
              <p className="font-bold text-charcoal">Envoyer un email de bienvenue</p>
              <p className="text-xs text-charcoal/60 mt-0.5">Email automatique avec les identifiants et la cle API, envoye via Resend. Le partenaire pourra acceder a son dashboard directement.</p>
            </div>
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={creatingPartner} className="btn-primary text-sm px-5 py-2 disabled:opacity-50" data-testid="submit-partner-btn">
              {creatingPartner ? 'Creation...' : 'Creer le partenaire'}
            </button>
            <button type="button" onClick={() => setShowCreatePartner(false)} className="btn-outline text-sm px-5 py-2">Annuler</button>
          </div>
        </form>
      )}

      {partnerCreated && (
        <div className="card-brutal mb-4 bg-secondary/20 space-y-3" data-testid="partner-created-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary border-2 border-charcoal flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-charcoal" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold">Partenaire cree avec succes</h3>
              <p className="text-sm text-charcoal/70">Communiquez ces informations au partenaire. La cle API ne sera plus affichee en entier ensuite.</p>
              {partnerCreated.welcome_email_sent && (
                <p className="text-sm text-charcoal mt-1 inline-flex items-center gap-1.5" data-testid="partner-created-email-sent">
                  <Check className="w-3.5 h-3.5 text-primary" /> <span className="font-medium">Email de bienvenue envoye a {partnerCreated.primary_admin?.email}</span>
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><span className="text-charcoal/60 w-24 shrink-0">Societe :</span><span className="font-bold">{partnerCreated.name}</span></div>
            <div className="flex items-center gap-2"><span className="text-charcoal/60 w-24 shrink-0">Email :</span><span className="font-mono text-xs">{partnerCreated.primary_admin?.email}</span></div>
            <div className="flex items-center gap-2"><span className="text-charcoal/60 w-24 shrink-0">Org ID :</span><span className="font-mono text-xs">{partnerCreated.org_id}</span></div>
            <div className="flex items-start gap-2">
              <span className="text-charcoal/60 w-24 shrink-0 pt-1.5">Cle API :</span>
              <div className="flex-1 flex items-center gap-2">
                <code className="font-mono text-xs bg-charcoal text-white px-2 py-1.5 rounded flex-1 break-all">{partnerCreated.api_key}</code>
                <button onClick={copyApiKey} className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1 shrink-0" data-testid="copy-partner-api-key">
                  {apiKeyCopied ? <><Check className="w-3 h-3" /> Copie</> : <><Copy className="w-3 h-3" /> Copier</>}
                </button>
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-2 border-t border-charcoal/10">
            <button onClick={() => { setPartnerCreated(null); setShowCreatePartner(false); }} className="btn-primary text-sm px-5 py-2" data-testid="partner-created-done">Termine</button>
            <button onClick={() => setPartnerCreated(null)} className="btn-outline text-sm px-5 py-2" data-testid="partner-created-another">Creer un autre</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center text-charcoal/60 py-8">Chargement...</p>
      ) : orgs.length === 0 ? (
        <div className="card-brutal text-center py-8">
          <Building2 className="w-12 h-12 mx-auto text-charcoal/40 mb-2" />
          <p className="text-charcoal/70">Aucune organisation</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {orgs.map((org) => {
            const used = org.pingme_count || 0;
            const max = org.max_pingmes || 100000;
            const pct = Math.min(100, Math.round((used / max) * 100));
            return (
              <div key={org.org_id} className="card-brutal cursor-pointer hover:shadow-brutal-xl hover:-translate-y-1 transition-all relative group" data-testid={`org-card-${org.org_id}`}>
                <div onClick={() => setSelected(org)} className="space-y-2">
                  <div className="flex items-center gap-2">
                    {org.branding?.logo_url ? (
                      <img src={org.branding.logo_url.startsWith('/api') ? `${API.replace('/api', '')}${org.branding.logo_url}` : org.branding.logo_url} alt="" className="w-10 h-10 rounded-lg border-2 border-charcoal object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-primary/20 rounded-lg border-2 border-charcoal flex items-center justify-center" style={{ backgroundColor: org.branding?.primary_color ? `${org.branding.primary_color}20` : undefined }}>
                        <Building2 className="w-5 h-5 text-charcoal" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading font-bold truncate">{org.name}</h4>
                      <p className="text-[10px] text-charcoal/50 font-mono truncate">{org.org_id}</p>
                    </div>
                    {org.use_case === 'partner' && (
                      <span className="bg-primary/30 text-charcoal text-[10px] font-bold px-2 py-0.5 rounded-full border border-charcoal/30">PARTENAIRE</span>
                    )}
                    {org.is_active === false && <span className="bg-danger/10 text-danger text-[10px] font-bold px-2 py-0.5 rounded-full border border-danger/30">INACTIF</span>}
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-charcoal/60 mb-1">
                      <span>{used.toLocaleString()} / {max.toLocaleString()}</span>
                      <span className="font-bold">{pct}%</span>
                    </div>
                    <div className="h-2 bg-charcoal/10 rounded-full overflow-hidden">
                      <div className={`h-full ${pct > 90 ? 'bg-danger' : pct > 70 ? 'bg-orange-400' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteOrg(org); }}
                  className="absolute top-2 right-2 p-1 text-charcoal/40 hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-org-${org.org_id}`}
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrganizationsTab;
